export enum RoleName {
  // Platform roles (Vera internal)
  PlatformAdmin = 'platform_admin',          // Level 1 - Full access
  PlatformManager = 'platform_manager',      // Level 2 - Limited platform access
  // VRG-89: additional internal Vera-staff roles.
  VeraFulfillment = 'vera_fulfillment',
  VeraFinance = 'vera_finance',
  // VRG-90: company-side "Vera support" access grant (company-typed, non-assignable;
  // its per-company permission set is what a visiting Vera staffer gets in-space).
  VeraSupport = 'vera_support',

  // Company roles (Client companies)
  CompanyAdmin = 'company_admin',            // Level 3 - Company administrator
  CompanyTeamMember = 'company_team_member', // Level 4 - Company team member
  // VERA-246: the fine-grained company roles already exist in the DB (roles table)
  // and carry their own permission sets. Adding them to the enum lets @RequireAdmin
  // authorize them (the guard's allowedRoles check rejects any role name not listed),
  // so permission grants to these roles actually take effect at the API.
  CompanyFinance = 'company_finance',
  CompanyDesign = 'company_design',
  CompanyAnalytics = 'company_analytics',
  CompanyMarketing = 'company_marketing',
  CompanyLogistics = 'company_logistics',
  CompanyPurchasing = 'company_purchasing',
  CompanyEngineering = 'company_engineering',
  CompanyLegal = 'company_legal',
  // VRG-565: shop / stockroom staff who confirm goods arrived. APP-ONLY — see
  // APP_ONLY_ROLE_NAMES below; this role can never obtain a platform session.
  CompanyLocationManager = 'company_location_manager',

  // Owner role (Certificate owners)
  Owner = 'owner',                           // Level 5 - Certificate owner
}

/**
 * Get user type based on role. Every platform/company role is an "admin"-type
 * user of the console; only the Owner role is a certificate owner.
 */
export function getUserType(roleName: RoleName): 'admin' | 'owner' {
  switch (roleName) {
    case RoleName.PlatformAdmin:
    case RoleName.PlatformManager:
    case RoleName.VeraFulfillment:
    case RoleName.VeraFinance:
    case RoleName.VeraSupport:
    case RoleName.CompanyAdmin:
    case RoleName.CompanyTeamMember:
    case RoleName.CompanyFinance:
    case RoleName.CompanyDesign:
    case RoleName.CompanyAnalytics:
    case RoleName.CompanyMarketing:
    case RoleName.CompanyLogistics:
    case RoleName.CompanyPurchasing:
    case RoleName.CompanyEngineering:
    case RoleName.CompanyLegal:
    // VRG-565: a Location manager is a company STAFF account (a `users` row with a
    // company role), not a certificate owner — so 'admin' is correct here even though
    // they never see the console. `getUserType` answers "which identity table is this",
    // not "which surface may they reach"; the surface question is APP_ONLY_ROLE_NAMES.
    case RoleName.CompanyLocationManager:
      return 'admin';
    case RoleName.Owner:
      return 'owner';
  }
}

/**
 * VRG-565 — roles whose ONLY surface is the VERA phone app. A member of this set can
 * never obtain or use a platform (console) session, regardless of which permissions
 * they hold.
 *
 * ── Why this is a property of the ROLE, not of the user ──────────────────────────────
 * "App-only" is grantable, visible wherever the role is, and impossible to forget on a
 * per-account basis. A `has_platform_access` column on `users` would have to be set
 * correctly at every provisioning path (invite, SSO auto-create, seed, migration
 * backfill) and would be silently wrong the first time one of them was missed. Keyed on
 * the role, a Location manager is app-only by construction the moment the role is
 * assigned.
 *
 * ── Why it is a SEPARATE axis from permissions ───────────────────────────────────────
 * `custody_receive` is a PERMISSION — *what* you may do. A company Admin or Logistics
 * user holds it too and confirms arrival from the platform's Confirm arrival button.
 * App-only is *where* you may act. Collapsing the two ("the role with custody_receive")
 * would make the Admin and Logistics roles app-only as a side effect, and would mean
 * revoking one permission silently re-opened the console.
 *
 * ── Precedent ────────────────────────────────────────────────────────────────────────
 * `vera_support` above is the existing role-level constraint orthogonal to permissions:
 * company-TYPED but non-assignable, expressed as `isAssignableCompanyRole` in the API's
 * `role.constants.ts`. Same shape — a predicate over the role name, one definition, read
 * by every surface that needs it.
 *
 * ── Where it is ENFORCED ─────────────────────────────────────────────────────────────
 * `AuthGuard` (apps/api) refuses the admin-token branch outright, so a bookmarked URL or
 * a cookie minted before the role was assigned is refused on the first API call rather
 * than rendering a half-built shell. `WorkOSAuthService.maybeGateLogin` refuses at
 * sign-in, before any token is minted, so the user gets APP_ONLY_ROLE_SIGN_IN_MESSAGE
 * instead of a broken console. `location-manager-role.guard.spec.ts` pins both.
 */
export const APP_ONLY_ROLE_NAMES: ReadonlySet<string> = new Set<string>([
  RoleName.CompanyLocationManager,
]);

/**
 * True when this role's only surface is the VERA phone app. Takes a raw role NAME
 * (nullable) rather than a `RoleName`, because every enforcement site reads the name off
 * a DB row or a JWT claim where the value is a plain string.
 */
export const isAppOnlyRole = (roleName?: string | null): boolean =>
  !!roleName && APP_ONLY_ROLE_NAMES.has(roleName);

/**
 * What an app-only account is told when it tries to sign into the platform.
 *
 * Names the VERA app as their surface rather than reading as a permissions failure: this
 * account is not under-privileged, it is on the wrong device. A bare "Forbidden" would
 * send a shop assistant to ask an admin for access that no admin can grant.
 */
export const APP_ONLY_ROLE_SIGN_IN_MESSAGE =
  'This account is for the VERA app. Location managers confirm arrivals by scanning in ' +
  'the VERA app on their phone, and cannot sign in to the web console. Open the VERA app ' +
  'and sign in there with this email address.';

/**
 * VRG-538 — roles that are NOT a buyer persona for market-content targeting.
 *
 * Lives here rather than in the API because BOTH apps need the derived list: the API
 * validates a tag write against it, the platform renders the picker from it. A second
 * copy of these exclusions in the client would be one more thing to keep in step with
 * this enum, and the drift would be silent — a role wrongly offered as a persona still
 * saves, still filters, and looks correct.
 *
 * The dimension answers "who at the BRAND are we talking to". Platform roles are VERA
 * staff — they do the outreach, they are never its target — so tagging content for them
 * is a category error. `owner` fails from the other end: a certificate owner is a brand's
 * CUSTOMER, not someone we sell to. `vera_support` is company-TYPED but is a Vera staffer
 * visiting a brand's space, so it fails the same test despite which half of the enum it
 * sits in.
 *
 * `company_location_manager` is KEPT — a shop supervisor confirming arrivals is a
 * plausible reader of logistics content, and excluding them would be a judgement about
 * their seniority rather than about whether they work for the brand.
 */
export const NON_PERSONA_ROLE_NAMES: ReadonlySet<string> = new Set<string>([
  RoleName.PlatformAdmin,
  RoleName.PlatformManager,
  RoleName.VeraFulfillment,
  RoleName.VeraFinance,
  RoleName.VeraSupport,
  RoleName.Owner,
]);

/**
 * The buyer personas content can be tagged for — DERIVED from `RoleName`, never restated.
 *
 * A role added to the enum appears here automatically, which is the strongest form of
 * "cannot drift" available: no guard is needed for additions, and the guard in
 * `market-taxonomies.guard.spec.ts` only has to police the exclusions above.
 */
export const TARGET_ROLE_NAMES: readonly string[] = Object.values(RoleName).filter(
  r => !NON_PERSONA_ROLE_NAMES.has(r),
);
